import { getList } from '@/requests/api/get-list';
import {
  type ErrandDelegationOffer,
  type ExternalSupplier,
  type GeneralErrand,
  type OrganizationUser,
} from '@pigello/pigello-matrix';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';
import { remove } from 'es-toolkit/compat';

// Custom handler for General Errand
export async function handleCustomGeneralErrandNestedCase(
  listResponse: TCustomList<GeneralErrand>
): Promise<
  TCustomList<
    GeneralErrand & {
      organizationUserDelegationOffer: ErrandDelegationOffer[];
      externalSupplierDelegationOffer: ErrandDelegationOffer[];
    }
  >
> {
  const errandIds = new Set<string>();
  for (const errand of listResponse.data) {
    errandIds.add(errand.id);
  }

  const chunkedErrandIds = chunkIds(errandIds);

  const resolvedDelegationPromises = await Promise.allSettled(
    chunkedErrandIds.map((chunk) =>
      getList<ErrandDelegationOffer>({
        modelName: 'erranddelegationoffer',
        queryParams: {
          filters: {
            general_errand: { __in: chunk.join(',') },
            answer: { noop: true },
          },
          pageSize: 500,
          order: ['-createdAt'],
        },
      })
    )
  );

  const resolvedDelegations: ErrandDelegationOffer[] = [];
  const failedDelegation: unknown[] = [];

  for (const delegation of resolvedDelegationPromises) {
    if (delegation.status === 'fulfilled') {
      resolvedDelegations.push(...delegation.value.list);
    } else {
      failedDelegation.push(delegation.reason);
    }
  }

  const orgUserIds = new Set<string>();
  const externalSupplierUserIds = new Set<string>();

  for (const delegation of resolvedDelegations) {
    if (delegation.organizationUser?.id) {
      orgUserIds.add(delegation.organizationUser.id);
    }
    if (delegation.externalSupplier?.id) {
      externalSupplierUserIds.add(delegation.externalSupplier.id);
    }
  }

  const chunkedOrgUserIds = chunkIds(orgUserIds);
  const chunkedExternalSupplierIds = chunkIds(externalSupplierUserIds);

  const orgUserPromise = Promise.allSettled(
    chunkedOrgUserIds.map((chunk) =>
      getList<OrganizationUser>({
        modelName: 'organizationuser',
        queryParams: {
          filters: {
            id: { __in: chunk.join(',') },
          },
        },
      })
    )
  );

  const externalSupplierPromise = Promise.allSettled(
    chunkedExternalSupplierIds.map((chunk) =>
      getList<ExternalSupplier>({
        modelName: 'externalsupplier',
        queryParams: {
          filters: {
            id: { __in: chunk.join(',') },
          },
        },
      })
    )
  );

  const [orgUsersResult, externalSuppliersResult] = await Promise.all([
    orgUserPromise,
    externalSupplierPromise,
  ]);

  const resolvedOrgUsers: OrganizationUser[] = [];
  const failedOrgUsers: unknown[] = [];

  for (const orgUser of orgUsersResult) {
    if (orgUser.status === 'fulfilled') {
      resolvedOrgUsers.push(...orgUser.value.list);
    } else {
      failedOrgUsers.push(orgUser.reason);
    }
  }

  const resolvedExternalSuppliers: ExternalSupplier[] = [];
  const failedExternalSuppliers: unknown[] = [];

  for (const externalSupplier of externalSuppliersResult) {
    if (externalSupplier.status === 'fulfilled') {
      resolvedExternalSuppliers.push(...externalSupplier.value.list);
    } else {
      failedExternalSuppliers.push(externalSupplier.reason);
    }
  }

  const mappedListRespones = {
    data: listResponse.data.map((errand) => {
      const errandDelegations = remove(
        resolvedDelegations,
        (delegation) => delegation.generalErrand?.id === errand.id
      );

      const organizationUserDelegationOffer: ErrandDelegationOffer[] = [];
      const externalSupplierDelegationOffer: ErrandDelegationOffer[] = [];

      for (const delegation of errandDelegations) {
        if (delegation.organizationUser?.id) {
          const orgUser =
            resolvedOrgUsers.find(
              (orgUser) => orgUser.id === delegation.organizationUser?.id
            ) ?? delegation.organizationUser;
          organizationUserDelegationOffer.push({
            ...delegation,
            organizationUser: orgUser,
          });
        }
        if (delegation.externalSupplier?.id) {
          const externalSupplier =
            resolvedExternalSuppliers.find(
              (externalSupplier) =>
                externalSupplier.id === delegation.externalSupplier?.id
            ) ?? delegation.externalSupplier;
          externalSupplierDelegationOffer.push({
            ...delegation,
            externalSupplier,
          });
        }
      }

      return {
        ...errand,
        organizationUserDelegationOffer,
        externalSupplierDelegationOffer,
      };
    }),
  };

  return mappedListRespones;
}
